import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Drawer, Typography } from '@mui/material';
import useResponsive from '../../../hooks/useResponsive';
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import navConfig from './config';

const NAV_WIDTH = 280;
const NAV_COLLAPSE_WIDTH = 88;

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
  isNavCollapsed: PropTypes.bool,
  onLogoClick: PropTypes.func,
  visualMode: PropTypes.bool,
};

export default function Nav({ openNav, onCloseNav, isNavCollapsed, onLogoClick, visualMode }) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');

  const navWidth = isDesktop ? (isNavCollapsed ? NAV_COLLAPSE_WIDTH : NAV_WIDTH) : NAV_WIDTH;

  useEffect(() => {
    if (openNav) onCloseNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box
        onClick={onLogoClick}
        title={visualMode ? 'Exit map view' : 'Toggle map view'}
        sx={{
          px: isDesktop && isNavCollapsed ? 0 : 2.5,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
          justifyContent: isDesktop && isNavCollapsed ? 'center' : 'flex-start',
          cursor: onLogoClick ? 'pointer' : 'default',
          outline: visualMode ? '2px solid' : 'none',
          outlineColor: 'primary.main',
          borderRadius: 1,
        }}
      >
        <Logo disabledLink />
        {!(isDesktop && isNavCollapsed) && (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>Measure</Typography>
        )}
      </Box>

      <NavSection
        data={navConfig}
        sx={
          isDesktop && isNavCollapsed
            ? {
                '& .MuiListItemText-root': { display: 'none' },
                '& .MuiListItemButton-root': { justifyContent: 'center' },
                '& .MuiListItemIcon-root': { minWidth: 0, mr: 0, justifyContent: 'center' },
              }
            : undefined
        }
      />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box component="nav" sx={{ flexShrink: { lg: 0 }, width: { lg: navWidth } }}>
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: { width: navWidth, bgcolor: 'background.default', borderRightStyle: 'dashed' },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: NAV_WIDTH } }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
